import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Months dropdown options
const months = [
    { value: 1, name: "January" }, { value: 2, name: "February" }, { value: 3, name: "March" },
    { value: 4, name: "April" }, { value: 5, name: "May" }, { value: 6, name: "June" },
    { value: 7, name: "July" }, { value: 8, name: "August" }, { value: 9, name: "September" },
    { value: 10, name: "October" }, { value: 11, name: "November" }, { value: 12, name: "December" }
];

// Returns current and previous year for dropdown
const getYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1];
};

// Initial empty state for stats
const initialStats = {
    TotalTenants: '',
    FicaCompliant: '',
    FicaNonCompliant: ''
};

const FicaInput = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [stats, setStats] = useState(initialStats);
    const [isLoading, setIsLoading] = useState(false);

    const { apiFetch } = useAuth();
    const years = getYears();

    // Disable future months if current year is selected
    const isMonthDisabled = (monthValue) => {
        if (selectedYear > currentYear) return false;
        if (selectedYear === currentYear && monthValue > currentMonth) return true;
        return false;
    };

    // Create period in YYYYMM format
    const getSelectedPeriod = () =>
        Number(`${selectedYear}${selectedMonth.toString().padStart(2, '0')}`);

    // Fetch stats for selected period
    useEffect(() => {
        const fetchStats = async () => {
            setIsLoading(true);
            try {
                const response = await apiFetch(`/api/Ops/TempTenantFicaStats?period=${getSelectedPeriod()}`);
                const data = await response.json();
                if (data.success && data.data) {
                    setStats({
                        TotalTenants: data.data.totalTenants ?? '',
                        FicaCompliant: data.data.ficaCompliant ?? '',
                        FicaNonCompliant: data.data.ficaNonCompliant ?? ''
                    });
                } else {
                    setStats(initialStats);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                setStats(initialStats);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
        // eslint-disable-next-line
    }, [selectedMonth, selectedYear]);

    // Handle input changes, only allow numbers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow numbers
        if (value === '' || /^\d+$/.test(value)) {
            setStats(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Save button handler
    const handleSave = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
                period: getSelectedPeriod(),
                totalTenants: stats.TotalTenants === '' ? null : parseInt(stats.TotalTenants, 10),
                ficaCompliant: stats.FicaCompliant === '' ? null : parseInt(stats.FicaCompliant, 10),
                ficaNonCompliant: stats.FicaNonCompliant === '' ? null : parseInt(stats.FicaNonCompliant, 10)
            };
            const response = await apiFetch('/api/Ops/TempTenantFicaStats', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Data saved successfully!');
            } else {
                toast.error('Failed to save data.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fica-input-container" style={{ paddingBottom: "15px" }}>
            <h1>FICA Input</h1>
            <div className="period-selector">
                <label>Month:</label>
                <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
                    {months.map(month => (
                        <option key={month.value} value={month.value} disabled={isMonthDisabled(month.value)}>
                            {month.name}
                        </option>
                    ))}
                </select>
                <label>Year:</label>
                <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
            </div>
            <form onSubmit={handleSave}>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Total Tenants in Portfolio</label>
                    <input
                        type="text"
                        name="TotalTenants"
                        value={stats.TotalTenants}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        placeholder="Total Tenants"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>FICA Compliant</label>
                    <input
                        type="text"
                        name="FicaCompliant"
                        value={stats.FicaCompliant}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        placeholder="FICA Compliant"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>FICA Non-Compliant</label>
                    <input
                        type="text"
                        name="FicaNonCompliant"
                        value={stats.FicaNonCompliant}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        placeholder="FICA Non-Compliant"
                    />
                </div>
                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                </button>
            </form>
</div>
    );
};

export default FicaInput;